import { Fragment, useMemo, useState } from 'react';
import type { EconCalcFact, EconSource } from '@shared/api';
import {
  factDimensionSegments,
  fmtPercent,
  labelMetric,
} from '@entities/economic/types';
import {
  IncompleteTag,
  MetricDelta,
  MetricDim,
  MetricLabel,
  MetricList,
  MetricNote,
  MetricValue,
  MoreToggle,
  Muted,
  ObjectGroup,
  ObjectHead,
  ObjectName,
} from './styles';
import {
  bucketByMetric,
  FACTS_VISIBLE_LIMIT,
  fmtMetricValue,
  groupFactsByObject,
  INCOMPLETE_REVENUE_METRICS,
  type MetricBucket,
  metricNoteFor,
} from './helpers';

/** Один показатель: название метрики + разрез (если есть) + значение и план.
 *  `omitDims` — оси, уже вынесенные в заголовок группы (объект), их в разрезе
 *  не дублируем. `note` — необязательное примечание-неполнота (вторичный текст
 *  под значением: «затраты не загружались — маржа равна доходу» и т.п.). */
export function MetricFactRow({
  fact,
  omitDims,
  note,
  revenueIncomplete,
}: {
  fact: EconCalcFact;
  omitDims: readonly string[];
  note?: string | null;
  /** Обработка неполна (не все источники вошли) — пометить итоговый доход. */
  revenueIncomplete: boolean;
}) {
  const segments = factDimensionSegments(fact.dimension, omitDims);
  const hasPlan = fact.value_plan !== null;
  // Метим только итоговые денежные показатели дохода и только при неполноте —
  // число оставляем видимым (экономисту полезно), но с явной оговоркой.
  const flagIncomplete = revenueIncomplete && INCOMPLETE_REVENUE_METRICS.has(fact.metric);
  return (
    <>
      <MetricLabel>
        {labelMetric(fact.metric)}
        {segments.length > 0 && (
          <MetricDim>
            {segments.map((s, i) => (
              <Fragment key={s.key}>
                {i > 0 && ' · '}
                {s.label}: <b>{s.value}</b>
              </Fragment>
            ))}
          </MetricDim>
        )}
        {note && <MetricNote>{note}</MetricNote>}
      </MetricLabel>
      <MetricValue>
        {fmtMetricValue(fact.metric, fact.value_fact)}
        {hasPlan && (
          <MetricDelta>
            план {fmtMetricValue(fact.metric, fact.value_plan)}
            {fact.delta_pct !== null && <> · {fmtPercent(fact.delta_pct)}</>}
          </MetricDelta>
        )}
        {flagIncomplete && (
          <IncompleteTag>заведомо неполный — часть источников не вошла</IncompleteTag>
        )}
      </MetricValue>
    </>
  );
}

/** Список фактов одной метрики со свёрткой длинного хвоста. До
 *  [`FACTS_VISIBLE_LIMIT`] — показываем все; сверх — топ-N + кнопка «ещё K».
 *  `note` — примечание-неполнота метрики (одинаково для всех её фактов): чтобы
 *  не дублировать его на каждой строке при множественном разрезе, показываем
 *  только под первым фактом bucket'а. */
export function MetricFactList({
  bucket,
  omitDims,
  note,
  revenueIncomplete,
}: {
  bucket: MetricBucket;
  omitDims: readonly string[];
  note?: string | null;
  revenueIncomplete: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const { facts } = bucket;
  const overflow = facts.length - FACTS_VISIBLE_LIMIT;
  const collapsible = overflow > 0;
  const shown = collapsible && !expanded ? facts.slice(0, FACTS_VISIBLE_LIMIT) : facts;
  return (
    <>
      {shown.map((f, i) => (
        <MetricFactRow
          key={f.id}
          fact={f}
          omitDims={omitDims}
          note={i === 0 ? note : null}
          revenueIncomplete={revenueIncomplete}
        />
      ))}
      {collapsible && (
        <MoreToggle type="button" aria-expanded={expanded} onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Свернуть' : `Показать ещё ${overflow}`}
        </MoreToggle>
      )}
    </>
  );
}

/** Блок «Результат»: показатели обработки, сгруппированные по договору. Это
 *  главный экран для пользователя (ТЗ п.3.3) — готовые цифры, без простыни
 *  извлечённых строк. Внутри договора факты разложены по метрике, отсортированы
 *  по сумме и подписаны разрезом (статья / объект / лицензия / способ оплаты).
 *  `sources` нужны для аннотаций неполноты (нет КС-3 / нет КС-2 в обработке). */
export function FactsResult({
  facts,
  sources,
  revenueIncomplete,
}: {
  facts: EconCalcFact[];
  sources: EconSource[];
  /** Обработка неполна — пометить итоговые денежные показатели дохода. */
  revenueIncomplete: boolean;
}) {
  const groups = useMemo(() => groupFactsByObject(facts), [facts]);
  if (facts.length === 0) {
    return <Muted>Показатели появятся после завершения обработки.</Muted>;
  }
  return (
    <div>
      {groups.map((group) => {
        // Объект вынесен в заголовок группы — не дублируем его в разрезе.
        const omitDims = group.hasObject ? (['object'] as const) : ([] as const);
        const buckets = bucketByMetric(group.facts);
        return (
          <ObjectGroup key={group.title}>
            <ObjectHead>
              <ObjectName>{group.title}</ObjectName>
            </ObjectHead>
            <MetricList>
              {buckets.map((bucket) => (
                <MetricFactList
                  key={bucket.metric}
                  bucket={bucket}
                  omitDims={omitDims}
                  note={metricNoteFor(bucket.metric, group.facts, sources)}
                  revenueIncomplete={revenueIncomplete}
                />
              ))}
            </MetricList>
          </ObjectGroup>
        );
      })}
    </div>
  );
}
